import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp, TrendingDown, History, Sparkles, Camera, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ShareArc from "@/components/ShareArc";

const V_COLOR = { GO: "#10B981", MAYBE: "#F59E0B", PASS: "#E76F51" };

export default function HistoryStrip({ leadId, refreshKey }) {
  const [data, setData] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    api.get(`/leads/${leadId}/history`).then((r) => { if (alive) setData(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [leadId, refreshKey]);

  if (!data || (data.points || []).length < 2) return null;

  const points = data.points;
  const first = points[0];
  const last = points[points.length - 1];

  // Build chart series — index by order (chronological)
  const chartData = points.map((p, i) => ({
    i: i + 1,
    profit: p.projected_profit ?? 0,
    mao: p.mao ?? 0,
    label: p.name,
    verdict: p.verdict,
  }));

  const profitDelta = (last.projected_profit ?? 0) - (first.projected_profit ?? 0);
  const verdictMoved = first.verdict !== last.verdict;
  const headline = buildHeadline(first, last, profitDelta, verdictMoved);

  return (
    <div className="card mb-5 print:hidden" data-testid="history-strip">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><History size={11} /> Story Arc</div>
          <h3 className="font-display font-bold text-lg mt-1">{headline}</h3>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" className="btn-money !py-2 !px-3 text-sm" onClick={() => setShareOpen(true)} data-testid="share-arc-button">
            <Share2 size={13} /> Share arc
          </button>
          <span className="chip" style={{ background: "rgba(15,23,42,0.06)", borderColor: "var(--line)", color: "var(--ink-2)", fontSize: 11 }}>
            {points.length} data points
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 items-stretch">
        {/* Verdict chain */}
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-semibold mb-2">Verdict path</div>
          <div className="flex items-center gap-2 flex-wrap">
            {points.map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span
                  className="chip"
                  style={{
                    background: (V_COLOR[p.verdict] || "#94A3B8") + "1f",
                    color: V_COLOR[p.verdict] || "var(--ink-2)",
                    borderColor: (V_COLOR[p.verdict] || "#94A3B8") + "55",
                    fontSize: 11, padding: "3px 8px",
                  }}
                  title={`${p.name} · ${new Date(p.created_at).toLocaleDateString()}`}
                >
                  <span className="chip-dot" style={{ background: V_COLOR[p.verdict] || "var(--ink-3)" }} />
                  {p.verdict || "—"}
                </span>
                {i < points.length - 1 && <span className="text-[var(--ink-3)]">→</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Profit delta */}
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-semibold mb-2">Profit delta</div>
          <div className="flex items-baseline gap-2">
            <span className="font-display font-bold text-xl tabular-nums">{fmtMoney(first.projected_profit)}</span>
            <span className="text-[var(--ink-3)]">→</span>
            <span className="font-display font-bold text-xl tabular-nums">{fmtMoney(last.projected_profit)}</span>
          </div>
          <div className={"flex items-center gap-1 mt-1 text-sm font-semibold " + (profitDelta >= 0 ? "text-[var(--primary)]" : "text-[var(--warn)]")}>
            {profitDelta >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {profitDelta >= 0 ? "+" : "−"}{fmtMoney(Math.abs(profitDelta))}
          </div>
        </div>

        {/* Sparkline */}
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-semibold mb-1">Projected profit · over snapshots</div>
          <div className="h-16">
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="i" hide />
                <YAxis hide domain={["dataMin", "dataMax"]} />
                <Tooltip
                  contentStyle={{ background: "var(--ink)", border: "none", borderRadius: 8, color: "#fff", fontSize: 11 }}
                  formatter={(v) => [fmtMoney(v), "Profit"]}
                  labelFormatter={(_, items) => items?.[0]?.payload?.label || ""}
                />
                <Line type="monotone" dataKey="profit" stroke="var(--primary)" strokeWidth={2} dot={{ r: 3, fill: "var(--primary)" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {shareOpen && (
        <ShareArc
          leadAddress={data.lead?.property_address || "Property"}
          leadCityState={[data.lead?.city, data.lead?.state].filter(Boolean).join(", ")}
          points={points}
          headline={headline}
          agentName={user?.name}
          brandLogo={user?.brand_logo}
          brandAccent={user?.brand_accent}
          brandCompany={user?.brand_company}
          leadId={leadId}
          reportToken={data.lead?.report_share_token}
          showWatermark={!!user?.watermark}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function buildHeadline(first, last, delta, verdictMoved) {
  const firstDate = niceDate(first.created_at);
  if (verdictMoved) {
    return `${firstDate}: ${first.verdict} at ${fmtMoney(first.projected_profit)} → Today: ${last.verdict} at ${fmtMoney(last.projected_profit)}.`;
  }
  if (Math.abs(delta) < 1000) {
    return `Steady at ${last.verdict} · profit holding around ${fmtMoney(last.projected_profit)} across ${niceCount(first, last)}.`;
  }
  if (delta > 0) {
    return `Profit climbed ${fmtMoney(delta)} since ${firstDate} — still ${last.verdict}.`;
  }
  return `Profit slipped ${fmtMoney(Math.abs(delta))} since ${firstDate} — verdict ${last.verdict}.`;
}

function niceDate(iso) {
  try {
    const d = new Date(iso);
    const days = Math.round((Date.now() - d.getTime()) / 86400000);
    if (days <= 1) return "Earlier today";
    if (days < 7) return `${days} days ago`;
    if (days < 60) return `${Math.round(days / 7)} weeks ago`;
    if (days < 365) return `${Math.round(days / 30)} months ago`;
    return d.toLocaleDateString();
  } catch { return "Earlier"; }
}

function niceCount(first, last) {
  const days = Math.round((new Date(last.created_at) - new Date(first.created_at)) / 86400000);
  if (days <= 1) return "today";
  if (days < 60) return `${days} days`;
  return `${Math.round(days / 30)} months`;
}
